import React, { useEffect, useState } from 'react';
import { Shell } from '../../components/layout/Shell';
import { api } from '../../lib/api';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { FitBadge, ScoreBars } from '../../components/hikari/ScoreBreakdown';
import { toast } from 'sonner';
import { GraduationCap, Target, Sparkle, ArrowRight, Share, Copy } from '@phosphor-icons/react';

const INTAKE_DISMISSED_KEY = 'hikari_intake_dismissed';

const CandidateDashboard = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareUrl, setShareUrl] = useState('');
  const [sharing, setSharing] = useState(false);
  const [intakeStatus, setIntakeStatus] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/candidates/me'),
      api.get('/assessments/attempts/mine'),
      api.get('/scoring/candidate/me'),
      api.get('/scoring/share/mine').catch(() => ({ data: [] })),
      api.get('/candidates/intake/status').catch(() => ({ data: null })),
    ])
      .then(([p, a, s, sh, ik]) => {
        setProfile(p.data);
        setAttempts(a.data);
        setScores(s.data);
        const active = (sh.data || []).find((x) => x.is_active);
        if (active) setShareUrl(`${window.location.origin}/s/${active.token}`);
        setIntakeStatus(ik.data);

        // First-time auto-redirect: never completed intake AND no headline/projects AND not dismissed
        const dismissed = (() => {
          try { return localStorage.getItem(INTAKE_DISMISSED_KEY) === '1'; } catch { return false; }
        })();
        if (ik.data && !ik.data.completed && !dismissed) {
          navigate('/candidate/intake', { replace: true });
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const latestScore = scores[0];
  const submittedAttempts = attempts.filter((a) => a.status === 'submitted');

  const shareScore = async () => {
    if (!latestScore) return;
    setSharing(true);
    try {
      const r = await api.post('/scoring/share', {
        score_id: latestScore.id,
        display_name: profile?.full_name || '',
      });
      const url = `${window.location.origin}/s/${r.data.token}`;
      setShareUrl(url);
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Share link copied to clipboard');
      } catch {
        toast.success('Share link created');
      }
    } catch {
      toast.error('Could not create share link');
    } finally {
      setSharing(false);
    }
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Copied!');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <Shell>
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <div className="label-eyebrow text-primary mb-2 flex items-center gap-2">
              CANDIDATE CONTROL ROOM
              {profile?.experience_tier === 'early_career' && (
                <span
                  className="label-eyebrow bg-accent text-foreground px-2 py-0.5"
                  data-testid="early-career-pill"
                  title="Soft scoring weights active: Performance 50% · Consistency 30% · Artifacts 20% · Reliability 0%"
                >
                  EARLY-CAREER MODE
                </span>
              )}
            </div>
            <h1 className="font-display font-black text-4xl tracking-tighter">
              Hello, {profile?.full_name || 'there'}.
            </h1>
          </div>
          <div className="flex gap-2">
            <Link to="/candidate/assessments"><Button className="rounded-none" data-testid="go-assessments">Take assessment <ArrowRight size={16} className="ml-2" /></Button></Link>
            <Link to="/candidate/jobs"><Button variant="outline" className="rounded-none" data-testid="go-jobs">Browse jobs</Button></Link>
          </div>
        </div>

        {/* Auto-suggest: if no projects + no credentials + no tier set, prompt early-career */}
        {!loading && profile &&
         !profile.experience_tier &&
         (profile.projects?.length || 0) === 0 &&
         (profile.credentials?.length || 0) === 0 && (
          <div
            className="bg-foreground text-background p-5 mb-6 border border-border flex items-center justify-between gap-4 flex-wrap"
            data-testid="early-career-suggest"
          >
            <div>
              <div className="label-eyebrow text-accent mb-1">FAIRER SCORING — ONE CLICK</div>
              <div className="font-display font-black text-lg tracking-tight">
                Just starting out? Turn on Early-career mode.
              </div>
              <div className="mono text-xs opacity-80 mt-1">
                Drops "work history" out of your fit score and lets your assessments + projects carry the weight.
              </div>
            </div>
            <Link to="/candidate/profile">
              <Button
                size="sm"
                className="rounded-none bg-accent text-foreground hover:bg-accent/90"
                data-testid="early-career-suggest-cta"
              >
                Set my career stage
              </Button>
            </Link>
          </div>
        )}

        {loading ? (
          <div className="mono text-sm text-muted-foreground">Loading...</div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-0 grid-skeleton">
            {/* Profile Strength */}
            <div className="lg:col-span-4 p-6 bg-white" data-testid="profile-card">
              <div className="flex items-center gap-2 mb-4">
                <GraduationCap size={20} weight="duotone" className="text-primary" />
                <div className="label-eyebrow">PROFILE</div>
              </div>
              <div className="space-y-2 mono text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Headline</span><span className="font-medium">{profile?.headline || '—'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Location</span><span className="font-medium">{profile?.location || '—'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Country</span><span className="font-medium">{profile?.country || '—'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Work auth</span><span className="font-medium">{profile?.work_authorization || '—'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Availability</span><span className="font-medium">{profile?.availability || '—'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Credentials</span><span className="font-medium">{profile?.credentials?.length || 0}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Projects</span><span className="font-medium">{profile?.projects?.length || 0}</span></div>
              </div>
              <Link to="/candidate/profile">
                <Button variant="outline" size="sm" className="rounded-none w-full mt-6" data-testid="edit-profile">
                  Edit profile
                </Button>
              </Link>
            </div>

            {/* Latest Score */}
            <div className="lg:col-span-4 p-6 bg-white" data-testid="latest-score-card">
              <div className="flex items-center gap-2 mb-4">
                <Target size={20} weight="duotone" className="text-primary" />
                <div className="label-eyebrow">LATEST FIT SCORE</div>
              </div>
              {latestScore ? (
                <>
                  <div className="flex items-center gap-4 mb-4">
                    <FitBadge value={latestScore.overall_fit} size="md" />
                    <div>
                      <div className="label-eyebrow text-muted-foreground">OVERALL</div>
                      <div className="mono font-bold text-3xl">{latestScore.overall_fit}/100</div>
                    </div>
                  </div>
                  <ScoreBars scores={latestScore} />

                  {/* Share your score — viral loop */}
                  <div className="mt-6 border-t border-border pt-4">
                    {shareUrl ? (
                      <div className="space-y-2" data-testid="share-link-block">
                        <div className="label-eyebrow text-muted-foreground">YOUR PUBLIC SHARE LINK</div>
                        <div className="flex gap-1">
                          <input
                            readOnly
                            value={shareUrl}
                            className="flex-1 border border-border px-2 py-1 text-xs font-mono bg-secondary"
                            onFocus={(e) => e.target.select()}
                            data-testid="share-url"
                          />
                          <Button size="sm" variant="outline" className="rounded-none" onClick={copyShare} data-testid="copy-share">
                            <Copy size={14} />
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Paste this into LinkedIn, a cold email, or your résumé.
                        </p>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        onClick={shareScore}
                        disabled={sharing}
                        className="rounded-none w-full"
                        data-testid="share-score-btn"
                      >
                        <Share size={14} className="mr-1" />
                        {sharing ? 'Creating link…' : 'Share my score publicly'}
                      </Button>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No score yet. Complete assessments and apply to a job to see your transparent fit score.
                </div>
              )}
            </div>

            {/* Assessment History */}
            <div className="lg:col-span-4 p-6 bg-white" data-testid="attempts-card">
              <div className="flex items-center gap-2 mb-4">
                <Sparkle size={20} weight="duotone" className="text-primary" />
                <div className="label-eyebrow">ASSESSMENT HISTORY</div>
              </div>
              {submittedAttempts.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  No attempts yet.
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {submittedAttempts.slice(0, 6).map((a) => (
                    <li key={a.id} className="py-3 flex items-center justify-between">
                      <div className="text-sm">
                        <div className="font-medium capitalize">{a.assessment_type}</div>
                        <div className="mono text-xs text-muted-foreground">{new Date(a.submitted_at).toLocaleDateString()}</div>
                      </div>
                      <div className="mono font-bold text-lg">{a.raw_score}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Work Style Summary */}
            {latestScore?.work_style_summary && (
              <div className="lg:col-span-12 p-8 bg-foreground text-background" data-testid="work-style-card">
                <div className="label-eyebrow text-accent mb-3">WORK-STYLE PROFILE</div>
                <p className="font-display text-2xl lg:text-3xl max-w-4xl leading-snug tracking-tight">
                  "{latestScore.work_style_summary}"
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </Shell>
  );
};

export default CandidateDashboard;
